import React, { useId, useRef, useState } from "react";
import {
    BACKPACK_OPTIONS,
    BENEFIT_DEFINITIONS,
    BUDGET_TIERS,
    DEFAULT_BACKPACK_SLOTS,
    normalizeBackpackSlots,
    getPreviousBudgetTier,
    KIT_BONUS_MIN_BUDGET,
    PREMIUM_MIN_BUDGET,
    RECYCLER_MODES,
    buildBudgetGapMessage,
    effectiveBackpackPrice,
    formatBackpackOptionLabel,
    isBenefitUnlocked,
} from "@/data/vipBenefitsConfig";
import { useI18n } from "@/i18n/I18nProvider";
import BonusKitInventoryPreview from "./BonusKitInventoryPreview";
import BudgetGapDialog from "./BudgetGapDialog";
import { formatBRL } from "./vipSimulatorUtils";

function BenefitToggle({ checked, disabled, onChange, id }) {
    return (
        <span
            id={id}
            role="checkbox"
            aria-checked={checked}
            aria-disabled={disabled}
            className={`vip-sim-benefit-toggle ${checked ? "vip-sim-benefit-toggle--on" : ""} ${
                disabled ? "vip-sim-benefit-toggle--disabled" : ""
            }`}
            tabIndex={disabled ? -1 : 0}
            onClick={(e) => {
                e.stopPropagation();
                if (!disabled) onChange(!checked);
            }}
            onKeyDown={(e) => {
                if (disabled) return;
                if (e.key === " " || e.key === "Enter") {
                    e.preventDefault();
                    e.stopPropagation();
                    onChange(!checked);
                }
            }}
        >
            <span className="vip-sim-benefit-toggle__knob" />
        </span>
    );
}

function KitBonusRow({ def, unlocked }) {
    const { t, l } = useI18n();
    const [hover, setHover] = useState(false);
    const wrapRef = useRef(null);

    return (
        <div
            className={`vip-sim-benefit-card vip-sim-benefit-card--kit ${
                unlocked ? "vip-sim-benefit-card--on" : "vip-sim-benefit-card--locked"
            }`}
            onMouseEnter={() => unlocked && setHover(true)}
            onMouseLeave={() => setHover(false)}
            ref={wrapRef}
        >
            <div className="vip-sim-benefit-card__main vip-sim-benefit-card__main--static">
                <span className="vip-sim-benefit-card__badge" aria-hidden>
                    🎁
                </span>
                <span className="vip-sim-benefit-card__text">
                    <span
                        className={`vip-sim-benefit-card__title ${
                            unlocked ? "vip-sim-benefit-card__title--hoverable" : ""
                        }`}
                    >
                        {l(def.title)}
                    </span>
                    <span className="vip-sim-benefit-card__meta">{l(def.description)}</span>
                    {unlocked ? (
                        <span className="vip-sim-benefit-card__meta vip-sim-benefit-card__meta--free">
                            {t("benefits.kit.included", { amount: KIT_BONUS_MIN_BUDGET })}
                        </span>
                    ) : (
                        <span className="vip-sim-benefit-card__meta">
                            {t("benefits.kit.freeFrom", { amount: KIT_BONUS_MIN_BUDGET })}
                        </span>
                    )}
                </span>
            </div>
            {unlocked && hover ? (
                <div className="vip-sim-bonus-kit-popover" role="tooltip">
                    <BonusKitInventoryPreview />
                </div>
            ) : null}
        </div>
    );
}

function BackpackRow({ def, state, budget, unlocked, onPatch }) {
    const { t, l, lang } = useI18n();
    const selectId = useId();
    const slots = normalizeBackpackSlots(state.backpackSlots ?? DEFAULT_BACKPACK_SLOTS);
    const price = effectiveBackpackPrice(slots, budget);

    return (
        <div
            className={`vip-sim-benefit-card ${state.enabled ? "vip-sim-benefit-card--on" : ""} ${
                !unlocked ? "vip-sim-benefit-card--locked" : ""
            }`}
        >
            <div className="vip-sim-benefit-card__row">
                <BenefitToggle
                    id={`${selectId}-toggle`}
                    checked={state.enabled}
                    disabled={!unlocked}
                    onChange={(enabled) => onPatch({ enabled })}
                />
                <span className="vip-sim-benefit-card__icon" aria-hidden>
                    🎒
                </span>
                <div className="vip-sim-benefit-card__text vip-sim-benefit-card__text--grow">
                    <span className="vip-sim-benefit-card__title">{l(def.title)}</span>
                    {def.description ? (
                        <span className="vip-sim-benefit-card__meta">{l(def.description)}</span>
                    ) : null}
                </div>
            </div>
            {state.enabled && unlocked ? (
                <div className="vip-sim-benefit-card__controls">
                    <label className="vip-sim-benefit-field__label" htmlFor={selectId}>
                        {t("benefits.capacity")}
                    </label>
                    <select
                        id={selectId}
                        className="vip-sim-benefit-select"
                        value={slots}
                        onChange={(e) => onPatch({ backpackSlots: Number(e.target.value) })}
                    >
                        {BACKPACK_OPTIONS.map((opt) => (
                            <option key={opt.slots} value={opt.slots}>
                                {formatBackpackOptionLabel(opt, budget, lang)}
                            </option>
                        ))}
                    </select>
                    {slots > DEFAULT_BACKPACK_SLOTS || price > 0 ? (
                        <span
                            className={`vip-sim-benefit-card__price-tag ${
                                price === 0 ? "vip-sim-benefit-card__price-tag--free" : ""
                            }`}
                        >
                            {price > 0 ? t("benefits.priceExtra", { price }) : t("benefits.freeTier")}
                        </span>
                    ) : (
                        <span className="vip-sim-benefit-card__meta">{t("benefits.defaultNoCost")}</span>
                    )}
                </div>
            ) : null}
        </div>
    );
}

function RecyclerRow({ def, state, budget, unlocked, onPatch }) {
    const { t, l } = useI18n();
    const groupId = useId();
    const needsBudget = def.minBudget != null && budget < def.minBudget;

    return (
        <div
            className={`vip-sim-benefit-card ${state.enabled ? "vip-sim-benefit-card--on" : ""} ${
                !unlocked ? "vip-sim-benefit-card--locked" : ""
            }`}
        >
            <div className="vip-sim-benefit-card__row">
                <BenefitToggle
                    checked={state.enabled}
                    disabled={!unlocked}
                    onChange={(enabled) => onPatch({ enabled })}
                />
                <span className="vip-sim-benefit-card__icon" aria-hidden>
                    ♻️
                </span>
                <div className="vip-sim-benefit-card__text vip-sim-benefit-card__text--grow">
                    <span className="vip-sim-benefit-card__title">{l(def.title)}</span>
                    <span className="vip-sim-benefit-card__meta">
                        {needsBudget
                            ? t("benefits.availableFrom", { amount: def.minBudget })
                            : l(def.description)}
                    </span>
                </div>
            </div>
            {state.enabled && unlocked ? (
                <fieldset className="vip-sim-benefit-radio-group" role="radiogroup" aria-labelledby={groupId}>
                    <legend id={groupId} className="vip-sim-benefit-field__label">
                        {t("benefits.mode")}
                    </legend>
                    {RECYCLER_MODES.map((mode) => (
                        <label key={mode.id} className="vip-sim-benefit-radio">
                            <input
                                type="radio"
                                name={`recycler-${groupId}`}
                                value={mode.id}
                                checked={state.recyclerMode === mode.id}
                                onChange={() => onPatch({ recyclerMode: mode.id })}
                            />
                            <span>{l(mode.label)}</span>
                        </label>
                    ))}
                </fieldset>
            ) : null}
        </div>
    );
}

function ToggleRow({ def, state, unlocked, lockedPremium, onPatch }) {
    const { t, l } = useI18n();
    const locked = def.premium && lockedPremium;
    let meta = l(def.description) ?? "";
    if (locked) meta = t("benefits.lockedBelow", { amount: PREMIUM_MIN_BUDGET });
    else if (def.premium) meta = t("benefits.premiumRequires", { amount: PREMIUM_MIN_BUDGET });
    else if (def.minBudget != null && !unlocked) {
        meta = t("benefits.availableFrom", { amount: def.minBudget });
    } else if (def.free) meta = meta || t("benefits.includedNoExtra");

    const emoji =
        def.id === "b_farm"
            ? "⛏️"
            : def.id === "b_sent"
              ? "🛡️"
              : def.id === "b_xp"
                ? "🪙"
                : def.id === "b_ai"
                  ? "🤖"
                  : def.id === "b_early"
                    ? "🚀"
                    : "";

    const disabled = !unlocked || locked;

    return (
        <div
            className={`vip-sim-benefit-card ${state.enabled ? "vip-sim-benefit-card--on" : ""} ${
                disabled ? "vip-sim-benefit-card--locked" : ""
            }`}
        >
            <button
                type="button"
                className="vip-sim-benefit-card__main"
                disabled={disabled}
                aria-pressed={state.enabled}
                onClick={() => !disabled && onPatch({ enabled: !state.enabled })}
            >
                <span
                    className={`vip-sim-benefit-toggle ${state.enabled ? "vip-sim-benefit-toggle--on" : ""}`}
                    aria-hidden
                >
                    <span className="vip-sim-benefit-toggle__knob" />
                </span>
                {emoji ? (
                    <span className="vip-sim-benefit-card__icon" aria-hidden>
                        {emoji}
                    </span>
                ) : null}
                <span className="vip-sim-benefit-card__text">
                    <span className="vip-sim-benefit-card__title">{l(def.title)}</span>
                    {meta ? <span className="vip-sim-benefit-card__meta">{meta}</span> : null}
                </span>
            </button>
        </div>
    );
}

export default function BenefitsPanel({
    budget,
    orderTotal,
    onBudgetChange,
    benefitState,
    onBenefitPatch,
    lockedPremium,
}) {
    const { t, l } = useI18n();
    const [gapOpen, setGapOpen] = useState(false);
    const [gapInfo, setGapInfo] = useState(null);
    const [pendingTier, setPendingTier] = useState(null);
    const budgetAboveTotal = budget > orderTotal + 0.001;

    const requestTier = (amount) => {
        if (amount < orderTotal - 0.001) {
            onBudgetChange(amount);
            return;
        }
        const gap = buildBudgetGapMessage(amount, orderTotal);
        if (gap) {
            setGapInfo(gap);
            setPendingTier(amount);
            setGapOpen(true);
            return;
        }
        onBudgetChange(amount);
    };

    const confirmGapTier = () => {
        if (pendingTier != null) onBudgetChange(pendingTier);
        setGapOpen(false);
        setGapInfo(null);
        setPendingTier(null);
    };

    return (
        <div className="vip-sim-benefits card-surface">
            <BudgetGapDialog
                open={gapOpen}
                onOpenChange={setGapOpen}
                onConfirm={confirmGapTier}
                gapInfo={gapInfo}
            />

            <div className="vip-sim-benefits__intro">
                <h2 className="vip-sim-panel-title">{t("benefits.title")}</h2>
                <p className="vip-sim-panel-lead">
                    {t("benefits.lead1")}
                    <strong>{t("benefits.leadNegotiable")}</strong>
                    {t("benefits.lead2")}
                </p>
            </div>

            <div className="vip-sim-budget card-surface card-surface--inset">
                <div className="vip-sim-budget__row">
                    <span className="vip-sim-budget__label">{t("benefits.budgetEstimated")}</span>
                    <span className="vip-sim-budget__val">R$ {budget}</span>
                </div>
                <div className="vip-sim-budget__row vip-sim-budget__row--sub">
                    <span className="vip-sim-budget__label">{t("benefits.totalEstimated")}</span>
                    <span className="vip-sim-budget__val vip-sim-budget__val--muted">
                        {formatBRL(orderTotal)}
                    </span>
                </div>
                {budgetAboveTotal ? (
                    <p className="vip-sim-budget__warn" role="status">
                        {t("benefits.budgetAboveWarn", { budget })}
                    </p>
                ) : budget < orderTotal - 0.001 ? (
                    <p className="vip-sim-budget__hint" role="status">
                        {t("benefits.budgetBelowHint", { budget, total: formatBRL(orderTotal) })}
                    </p>
                ) : null}

                <p className="vip-sim-budget__section-label">{t("benefits.tierSection")}</p>
                <div className="vip-sim-budget-tiers" role="radiogroup" aria-label={t("benefits.tierAria")}>
                    {BUDGET_TIERS.map((tier) => {
                        const selected = budget === tier.amount;
                        const prevTier = getPreviousBudgetTier(tier.amount);
                        const items = tier.exclusiveUnlocks ?? [];
                        return (
                            <button
                                key={tier.amount}
                                type="button"
                                role="radio"
                                aria-checked={selected}
                                className={`vip-sim-budget-tier ${selected ? "vip-sim-budget-tier--active" : ""}`}
                                onClick={() => requestTier(tier.amount)}
                            >
                                <span className="vip-sim-budget-tier__amount">R$ {tier.amount}</span>
                                {prevTier != null ? (
                                    <p className="vip-sim-budget-tier__inherits">
                                        {t(items.length ? "benefits.tierInherits" : "benefits.tierInheritsOnly", {
                                            prev: prevTier,
                                        })}
                                    </p>
                                ) : null}
                                <ul className="vip-sim-budget-tier__unlocks">
                                    {items.map((u) => (
                                        <li key={u.pt}>{l(u)}</li>
                                    ))}
                                </ul>
                            </button>
                        );
                    })}
                </div>
            </div>

            <ul className="vip-sim-benefit-cards">
                {BENEFIT_DEFINITIONS.map((def) => {
                    const state = benefitState[def.id] ?? { enabled: false };
                    const unlocked = isBenefitUnlocked(def, budget, lockedPremium);
                    const onPatch = (patch) => onBenefitPatch(def.id, patch);

                    return (
                        <li key={def.id}>
                            {def.kind === "info" ? (
                                <KitBonusRow def={def} unlocked={unlocked} />
                            ) : def.kind === "backpack" ? (
                                <BackpackRow
                                    def={def}
                                    state={state}
                                    budget={budget}
                                    unlocked={unlocked}
                                    onPatch={onPatch}
                                />
                            ) : def.kind === "recycler" ? (
                                <RecyclerRow
                                    def={def}
                                    state={state}
                                    budget={budget}
                                    unlocked={unlocked}
                                    onPatch={onPatch}
                                />
                            ) : (
                                <ToggleRow
                                    def={def}
                                    state={state}
                                    unlocked={unlocked}
                                    lockedPremium={lockedPremium}
                                    onPatch={onPatch}
                                />
                            )}
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}
